import { z } from 'zod';
import {
  medicalLetterRecipientTypes,
  medicalLetterStatuses,
  medicalLetterTypes,
  MEDICAL_LETTER_BODY_MAX,
  MEDICAL_LETTER_SUBJECT_MAX,
  type MedicalLetterRecipientType,
  type MedicalLetterSource,
  type MedicalLetterStatus,
  type MedicalLetterType,
} from '../enums.js';
import { optionalText, trimmed } from './_fields.js';

/**
 * Courriers médicaux — contrat réseau.
 *
 * La fonctionnalité existait en V1, a disparu de la V2 et n'est décrite dans aucun écran de
 * `docs/SCREENS.md` : c'est le seul trou de la spécification elle-même
 * (`docs/audit/parite-v1-v2.md` §1.1.1). Elle est reconstruite ici avec ce que la V1 promettait
 * sans le tenir — un envoi réel, un PDF téléchargeable depuis la liste — et sans ce qu'elle faisait
 * de trop : le prompt entier gardé en base et renvoyé au navigateur.
 */

/* -------------------------------------------------------------------------- */
/* Champs communs                                                              */
/* -------------------------------------------------------------------------- */

const recipientFields = {
  recipientType: z.enum(medicalLetterRecipientTypes),
  /** Nom du correspondant. Il n'y a pas de carnet d'adresses : il se saisit sur le courrier. */
  recipientName: optionalText(200).optional(),
  /** Sans adresse, le courrier s'imprime mais ne s'envoie pas. */
  recipientEmail: z.email().max(320).nullable().optional(),
  recipientAddress: optionalText(500).optional(),
};

const contentFields = {
  letterType: z.enum(medicalLetterTypes),
  subject: trimmed(MEDICAL_LETTER_SUBJECT_MAX),
  body: trimmed(MEDICAL_LETTER_BODY_MAX),
};

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

/** Rédaction à la main. La génération assistée a sa propre route et son propre contrat. */
export const medicalLetterCreateSchema = z
  .object({
    patientId: z.uuid(),
    /** Consultation d'origine, quand le courrier en vient. */
    encounterId: z.uuid().nullable().optional(),
    ...contentFields,
    ...recipientFields,
  })
  .strict();
export type MedicalLetterCreateInput = z.infer<typeof medicalLetterCreateSchema>;

/**
 * Modification, **tant que le courrier est un brouillon**. Ni le dossier ni la consultation n'y
 * figurent : un courrier ne change pas de patient. La V1 passait `req.body` brut au service, si
 * bien qu'un client pouvait repointer son courrier sur le cabinet du voisin.
 */
export const medicalLetterUpdateSchema = z
  .object({
    letterType: contentFields.letterType.optional(),
    subject: contentFields.subject.optional(),
    body: contentFields.body.optional(),
    recipientType: recipientFields.recipientType.optional(),
    recipientName: recipientFields.recipientName,
    recipientEmail: recipientFields.recipientEmail,
    recipientAddress: recipientFields.recipientAddress,
  })
  .strict();
export type MedicalLetterUpdateInput = z.infer<typeof medicalLetterUpdateSchema>;

/**
 * Génération assistée. Le corps n'est pas fourni : c'est ce qu'on demande. `instructions` est le
 * mot du praticien — « insister sur la reprise du sport » —, la seule entrée libre qui parte.
 *
 * La V1 avait le même champ côté client sous le nom `additionalContext` et le serveur lisait
 * `observations` : il n'arrivait jamais, sans erreur ni avertissement.
 */
export const medicalLetterGenerateSchema = z
  .object({
    patientId: z.uuid(),
    encounterId: z.uuid().nullable().optional(),
    letterType: contentFields.letterType,
    subject: contentFields.subject,
    ...recipientFields,
    instructions: optionalText(2000).optional(),
  })
  .strict();
export type MedicalLetterGenerateInput = z.infer<typeof medicalLetterGenerateSchema>;

/** Passage de brouillon à finalisé, puis envoi. Le sens inverse n'existe pas. */
export const medicalLetterFinalizeSchema = z.object({}).strict();

/**
 * Envoi par email. `to` n'est là que pour servir une autre adresse que celle du courrier, comme
 * pour une facture ; sans l'une ni l'autre, l'envoi est refusé plutôt que silencieux.
 */
export const medicalLetterSendSchema = z
  .object({
    to: z.email().max(320).optional(),
    message: optionalText(2000).optional(),
  })
  .strict();
export type MedicalLetterSendInput = z.infer<typeof medicalLetterSendSchema>;

export const medicalLetterIdParamSchema = z.object({ id: z.uuid() }).strict();

/** Filtre de la liste d'un dossier. Plusieurs états, comme la liste des factures. */
export const medicalLetterStatusFilterSchema = z
  .string()
  .transform((value) => value.split(',').map((part) => part.trim()))
  .pipe(z.array(z.enum(medicalLetterStatuses)).min(1).max(medicalLetterStatuses.length));

export const medicalLetterListQuerySchema = z
  .object({ status: medicalLetterStatusFilterSchema.optional() })
  .strict();
export type MedicalLetterListQuery = z.infer<typeof medicalLetterListQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export interface MedicalLetterDto {
  id: string;
  patientId: string;
  encounterId: string | null;
  letterType: MedicalLetterType;
  subject: string;
  recipientType: MedicalLetterRecipientType;
  recipientName: string | null;
  recipientEmail: string | null;
  recipientAddress: string | null;
  body: string;
  status: MedicalLetterStatus;
  /** Ce qui a produit le corps. La charge utile envoyée au fournisseur n'est jamais conservée. */
  source: MedicalLetterSource;
  aiModel: string | null;
  generatedAt: string | null;
  /** Impression rangée au dossier, à télécharger par la route des documents. */
  documentId: string | null;
  sentAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MedicalLetterListDto {
  items: MedicalLetterDto[];
}

/** Ce que rend l'envoi : le courrier à jour et l'impression qui est partie en pièce jointe. */
export interface MedicalLetterSendResultDto {
  letter: MedicalLetterDto;
  documentId: string;
}
